import './headless-env.ts';
import integrationJson from '../../../recipes/art/painted/actors/integration.json' with { type: 'json' };
// One line per zone whose plates live under recipes/art/painted/zones/<zone>/.
import z01Integration from '../../../recipes/art/painted/zones/Z01/integration.json' with { type: 'json' };
import z02Integration from '../../../recipes/art/painted/zones/Z02/integration.json' with { type: 'json' };
import z06Integration from '../../../recipes/art/painted/zones/Z06/integration.json' with { type: 'json' };
import mysteryIntegration from '../../../recipes/art/painted/mystery/integration.json' with { type: 'json' };
import Phaser from 'phaser';
import { SpriteManifestRegistry, type SpriteFrameInfo, type SpriteManifest } from './manifest.ts';
import { actorFrame, type ActorClipName, type ActionWindow } from './actor-motion.ts';
import { Ambient } from './ambient.ts';
export { burnings } from './ambient.ts';
import { CombatEffects, type BurstKind } from './combat-effects.ts';
import { MeleeContact } from './melee-contact.ts';
export type { ActionWindow } from './actor-motion.ts';

export type Point = readonly [number, number];
export interface PaintedPlate {
  id: string;
  image: string;
  width: number;
  height: number;
  foregrounds: readonly { polygon: readonly Point[]; depth: number; fromProp?: string }[];
  lamps: readonly Point[];
  water: readonly (readonly Point[])[];
  smoke: readonly Point[];
  /** Set dressing the story shows or hides (a waiting wagon, a handcart): an image standing at its foot point. */
  props?: readonly { id: string; image: string; at: Point; height?: number; depth?: number; crop?: readonly [number, number, number, number] }[];
}
export interface PaintedActor {
  id: string; x: number; y: number; appearance: string; facing: string;
  moving: boolean; dead: boolean; attacking: boolean; flash: number;
  action?: ActionWindow;
  guard?: Point;
  activity?: 'idle' | 'work';
}
interface Figure {
  sprite: Phaser.GameObjects.Sprite;
  shadow: Phaser.GameObjects.Image;
  ring: Phaser.GameObjects.Ellipse;
  skin: Phaser.Textures.CanvasTexture | undefined;
  light: string; warmth: number; daylight: number; fog: number;
  telegraph: Phaser.GameObjects.Graphics;
  x: number; y: number; distance: number; elapsed: number;
  clip: ActorClipName; dead: boolean; attacking: boolean; flash: number;
  impactAge: number; knock: Point; flashPower: number; deadFor: number; dim: number; facing: string; action: ActionWindow | undefined;
  appearance: string; frame: number; seen: number;
  pose: { frame: number; flipX: boolean }; snapshot: ActorMotionSnapshot;
  drawnAction: ActionWindow | undefined; telegraphX: number; telegraphY: number;
  drawnGuard?: Point | undefined;
  spriteInfo: SpriteFrameInfo | undefined; infoFacing: string;
}
export interface ActorMotionSnapshot {
  id: string; x: number; y: number; footX: number; footY: number;
  frame: number; clip: ActorClipName; depth: number;
  shadowX: number; shadowY: number; scale: number; flipX: boolean;
}
export interface PaintedGuidance {
  path: readonly Point[];
  target: Point | null;
  targetId?: string;
  telegraphs: readonly { from: Point; to: Point; progress: number; hostile: boolean }[];
}
export interface PaintedPerformanceStats {
  fps: number; drawCount: number; displayObjects: number;
  actors: number; visibleActors: number; culledActors: number;
}

/** `lift`: per-plate RGB offset on actors, so figures sit in the plate's light and value (decisions-palette.md). */
interface Integration { perspective: readonly [number, number, number, number]; ground: Point; haze: Point; fog: number; desaturation?: number; lift?: readonly [number, number, number] }
const INTEGRATION = { ...integrationJson, ...z01Integration, ...z02Integration, ...z06Integration, ...mysteryIntegration } as unknown as Readonly<Record<string, Integration>>;
const DEFAULT_INTEGRATION: Integration = { perspective: [0, 1024, 1, 1], ground: [0, 0], haze: [0, 0], fog: 0 };

type ProfileTarget = HTMLElement & { __paintedPerformance?: PaintedPerformanceStats };

/** Painted geometry and effects are display-only. Navigation and actions stay in sim. */
/** Camera zoom during a fight; exploration stays at the wider view. */
const COMBAT_ZOOM = 2.1;
/** Travel between paintings dissolves the last one away while the new one rises from this warm dark, the colour of dried ink. */
const FADE = [18, 14, 11] as const;
export class PaintedRenderer {
  #game: Phaser.Game;
  #scene: Phaser.Scene | null = null;
  #registry: SpriteManifestRegistry;
  #plate: PaintedPlate;
  #plates: readonly PaintedPlate[];
  #figures = new Map<string, Figure>();
  #scenery: Phaser.GameObjects.GameObject[] = [];
  #ambient: Ambient | null = null;
  #guidanceGraphics: Phaser.GameObjects.Graphics | null = null;
  #pathGraphics: Phaser.GameObjects.Graphics | null = null;
  #targetRing: Phaser.GameObjects.Ellipse | null = null;
  #markerGraphics: Phaser.GameObjects.Graphics | null = null;
  #cart: Phaser.GameObjects.Image | null = null;
  #cartTween: Phaser.Tweens.Tween | null = null;
  #lift: Phaser.GameObjects.Image | null = null;
  #liftTween: Phaser.Tweens.Tween | null = null;
  #liftRopes: Phaser.GameObjects.Rectangle[] = [];
  #shutters: Phaser.GameObjects.Image | null = null;
  #actors: readonly PaintedActor[] = [];
  #focus: string;
  #time = 0;
  #paused = false;
  #reducedMotion = false;
  #marker: Point | null = null;
  #snap = true;
  #zoom = 1.45;
  #look: Point | null = null;
  #overview = false;
  #minimumZoom = .5;
  #fighting = false;
  /** Places already shown whole this session, and the pending ease from that wide view into the walking view. */
  #established = new Set<string>();
  #establishing: Phaser.Time.TimerEvent | null = null;
  /** A directed shot (the opening) holds the camera on this point, from the whole painting (0) to walking distance (1). */
  #direct: { at: Point; closeness: number } | null = null;
  #props = new Map<string, Phaser.GameObjects.Image>();
  #propForegrounds: { prop: string; image: Phaser.GameObjects.Image; original: string; prepared: string }[] = [];
  #shownProps: readonly string[] = [];
  #guidance: PaintedGuidance = { path: [], target: null, telegraphs: [] };
  #world: { carts: string; shutters: string; gate: string; activeBodies: number } | null = null;
  #stop = 0;
  #impacts = new Map<string, { from?: Point; strength: number; flash: number }>();
  #contact = new MeleeContact();
  #effects: CombatEffects | null = null;
  #snapshots: ActorMotionSnapshot[] = [];
  #snapshotPools = new Map<number, { cursor: number; buffers: ActorMotionSnapshot[][] }>();
  #drawId = 0;
  #integration = DEFAULT_INTEGRATION;
  #ground = [100, 90, 78];
  #haze = [83, 94, 104];
  /** Source pixels per identity, which no plate changes, and each drawn cell's graded variants per plate. */
  #sources = new Map<string, Uint8ClampedArray>();
  #cells = new Map<string, Record<string, HTMLCanvasElement>>();
  /** Plates whose graded cells are kept, most recent last: walking back to a place does not grade its figures again. */
  #gradedPlates: string[] = [];
  #target: ProfileTarget;
  #performance: PaintedPerformanceStats = {
    fps: 0, drawCount: 0, displayObjects: 0, actors: 0, visibleActors: 0, culledActors: 0,
  };

  constructor(target: HTMLElement, plates: readonly PaintedPlate[], manifest: SpriteManifest, focus: string) {
    this.#target = target;
    delete target.dataset.ready; // a new life's renderer is ready only once its own scene is drawn
    this.#target.__paintedPerformance = this.#performance;
    this.#registry = new SpriteManifestRegistry(manifest);
    this.#plate = plates[0]!;
    this.#plates = plates;
    this.#focus = focus;
    const self = this;
    this.#game = new Phaser.Game({
      // Painted plates need only 2D compositing. Canvas avoids software WebGL on
      // machines without a GPU while retaining Phaser cameras, sprites and depth.
      type: Phaser.CANVAS, parent: target, width: 1280, height: 800, banner: false,
      // Our own canvas, never one from Phaser's page-wide CanvasPool: disposing a game returns a pooled main canvas,
      // whose opaque context a later life's game would reuse for a texture (the contact shadow drew as black boxes).
      canvas: document.createElement('canvas'),
      audio: { noAudio: true }, backgroundColor: '#14181c',
      render: { pixelArt: false, preserveDrawingBuffer: true },
      scene: {
        preload(this: Phaser.Scene) {
          this.load.image('painted-cart', 'assets/painted/props/z03-porter-handcart.png');
          for (const plate of plates) {
            this.load.image(plate.id, plate.image);
            for (const prop of plate.props ?? []) this.load.image(`prop:${prop.id}`, prop.image.replace(/^\//, ''));
          }
          for (const entry of self.#registry.entries()) {
            if (!entry.appearance.startsWith('actor-') && entry.appearance !== 'prop-crate-stack') continue;
            const animation = entry.animation;
            if (animation) this.load.spritesheet(`actor:${entry.appearance}`, animation.path.replace(/^\//, ''), {
              frameWidth: animation.cell.width, frameHeight: animation.cell.height });
            else {
              const info = self.#registry.resolve(entry.appearance, 's');
              if (info.path) this.load.spritesheet(info.textureKey, info.path.replace(/^\//, ''), { frameWidth: info.width, frameHeight: info.height });
            }
          }
        },
        create(this: Phaser.Scene) {
          self.#scene = this;

          self.#ambient = new Ambient(this);
          this.textures.createCanvas('painted-shadow', 96, 40);
          const shutters = this.textures.createCanvas('painted-shutters', 48, 40)!;
          shutters.context.fillStyle = 'rgba(50,45,40,.94)'; shutters.context.fillRect(0, 0, 45, 39);
          shutters.context.strokeStyle = '#655744'; shutters.context.lineWidth = 2;
          for (let x = 2; x < 45; x += 10) { shutters.context.beginPath(); shutters.context.moveTo(x, 1); shutters.context.lineTo(x, 37); shutters.context.stroke(); }
          shutters.refresh();
          self.setPlate(self.#plate.id);
          target.dataset.ready = 'true';
        },
        update(_time: number, delta: number) { self.#draw(Math.min(delta, 50)); },
      },
    });
  }

  /** Canvas has no sprite tint. Grade once per identity, pose cell and plate, the first time the cell is drawn, so a
   * scene change never grades whole sheets in one frame; a small cached pose blends lamp strength without washing out the base. */
  #skin(appearance: string, frame: number): Record<string, HTMLCanvasElement> {
    const key = `${this.#plate.id}|${appearance}:${frame}`, cached = this.#cells.get(key);
    if (cached) return cached;
    const scene = this.#scene!, animation = this.#registry.animation(appearance)!;
    const source = scene.textures.get(`actor:${appearance}`).getSourceImage() as HTMLImageElement, width = source.width;
    let original = this.#sources.get(appearance);
    if (!original) {
      const canvas = document.createElement('canvas'); canvas.width = width; canvas.height = source.height;
      const context = canvas.getContext('2d')!; context.drawImage(source, 0, 0);
      original = context.getImageData(0, 0, width, source.height).data;
      this.#sources.set(appearance, original);
    }
    const { width: w, height: h } = animation.cell, left0 = frame % (width / w) * w, top0 = Math.floor(frame / (width / w)) * h;
    const names = ['cool', 'left', 'right', 'fog', 'flash', 'day'], canvases = names.map(() => Object.assign(document.createElement('canvas'), { width: w, height: h }));
    const images = canvases.map(canvas => canvas.getContext('2d')!.createImageData(w, h));
    const [cool, left, right, fog, flash, day] = images.map(image => image.data) as [Uint8ClampedArray, Uint8ClampedArray, Uint8ClampedArray, Uint8ClampedArray, Uint8ClampedArray, Uint8ClampedArray];
    const groundMean = (this.#ground[0]! + this.#ground[1]! + this.#ground[2]!) / 3;
    const desaturation = this.#integration.desaturation ?? 0, row = width * 4, neighbours = [-row, -4, 4, row];
    const offset = this.#integration.lift ?? [0, 0, 0], tint = [0, 1, 2].map(channel => (this.#ground[channel]! - groundMean) * .10), shift = [-3, 0, 3];
    const gain = [.42, .24, .10], lift = [12, 7, 2], flashColour = [250, 172, 132], haze = this.#haze;
    const neighbour = [0, 0, 0];
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
      const i = ((top0 + y) * width + left0 + x) * 4, o = (y * w + x) * 4;
      const alpha = original[i + 3]!;
      if (!alpha) continue;
      const edge = alpha < 245;
      // One-pixel coverage edge only. Opaque cloth stays opaque; transparent
      // RGB never enters the filter, avoiding pale/black resampling halos.
      let weight = 0; neighbour[0] = neighbour[1] = neighbour[2] = 0;
      if (edge) for (const offset of neighbours) {
        const j = i + offset, a = original[j + 3] ?? 0;
        if (a < 245) continue;
        weight++; neighbour[0] += original[j]!; neighbour[1] += original[j + 1]!; neighbour[2] += original[j + 2]!;
      }
      const leftLight = Math.max(0, Math.min(1, (w * .64 - x) / (w * .28))), rightLight = Math.max(0, Math.min(1, (x - w * .36) / (w * .28)));
      const foot = Math.max(0, Math.min(1, (y - animation.foot[1] + 4) / 4)), shade = 1 - foot * .18;
      // Preserve material contrast: cool shadow, warm reflected stone,
      // and directional key light affect RGB, never body transparency.
      const luminance = original[i]! * .299 + original[i + 1]! * .587 + original[i + 2]! * .114;
      // Pale-floor bounce lifts lit material, not the creases and silhouette that give a figure weight.
      const bounce = Math.min(1, luminance / 110);
      for (let channel = 0; channel < 3; channel++) {
        const sourceValue = original[i + channel]!;
        const clean = weight ? sourceValue * .75 + neighbour[channel]! / weight * .25 : sourceValue;
        const material = clean * (1 - desaturation) + luminance * desaturation;
        const ambient = (material - 80) * 1.08 + 74 + tint[channel]! + offset[channel]! * bounce + (groundMean - 100) * .045 + shift[channel]!;
        cool[o + channel] = ambient * shade;
        left[o + channel] = (ambient * (1 + leftLight * gain[channel]!) + leftLight * lift[channel]!) * shade;
        right[o + channel] = (ambient * (1 + rightLight * gain[channel]!) + rightLight * lift[channel]!) * shade;
        day[o + channel] = (ambient * (1.2 + channel * .035) + 9 + channel * 2) * shade;
        fog[o + channel] = haze[channel]!;
        flash[o + channel] = flashColour[channel]!;
      }
      cool[o + 3] = left[o + 3] = right[o + 3] = fog[o + 3] = flash[o + 3] = day[o + 3] = alpha * (edge ? .94 : 1) * (1 - foot * .10);
    }
    canvases.forEach((canvas, index) => canvas.getContext('2d')!.putImageData(images[index]!, 0, 0));
    const cell = Object.fromEntries(names.map((name, index) => [name, canvases[index]!]));
    this.#cells.set(key, cell);
    return cell;
  }

  #samplePlate(at: Point): number[] {
    const canvas = document.createElement('canvas'); canvas.width = canvas.height = 1;
    const c = canvas.getContext('2d')!;
    c.drawImage(this.#scene!.textures.get(this.#plate.id).getSourceImage() as HTMLImageElement,
      at[0] - 8, at[1] - 8, 16, 16, 0, 0, 1, 1);
    return Array.from(c.getImageData(0, 0, 1, 1).data).slice(0, 3);
  }

  #distance(y: number): number {
    // ponytail: one depth curve per plate; split by ground surface if a new
    // elevated route needs an independent architectural scale.
    const [far, near] = this.#integration.perspective;
    return Math.max(0, Math.min(1, (y - far) / (near - far)));
  }

  #contactTexture(): void {
    const texture = this.#scene!.textures.get('painted-shadow') as Phaser.Textures.CanvasTexture;
    const c = texture.context, colour = this.#ground.map(value => Math.round(value * .18)).join(',');
    c.setTransform(1, 0, 0, 1, 0, 0); c.clearRect(0, 0, 96, 40);
    c.translate(48, 20); c.scale(1, .38);
    const shade = c.createRadialGradient(0, 0, 0, 0, 0, 46);
    shade.addColorStop(0, `rgba(${colour},.55)`); shade.addColorStop(.28, `rgba(${colour},.25)`); shade.addColorStop(1, `rgba(${colour},0)`);
    c.fillStyle = shade; c.fillRect(-48, -52, 96, 104);
    const contact = c.createRadialGradient(0, 0, 0, 0, 0, 15);
    contact.addColorStop(0, `rgba(${colour},.4)`); contact.addColorStop(1, `rgba(${colour},0)`);
    c.fillStyle = contact; c.fillRect(-16, -16, 32, 32); texture.refresh();
  }

  #removeFigure(figure: Figure): void {
    for (const object of [figure.sprite, figure.shadow, figure.ring, figure.telegraph]) object.destroy();
    figure.skin?.destroy();
  }

  setPlate(id: string): void {
    this.#plate = this.#plates.find(plate => plate.id === id)!;
    const scene = this.#scene;
    if (!scene) return;
    if (!this.#reducedMotion) this.#dissolve();
    scene.tweens.killTweensOf(this.#scenery);
    this.#scenery.forEach(object => object.destroy()); this.#scenery = [];
    this.#guidanceGraphics = this.#pathGraphics = this.#markerGraphics = null; this.#effects = null;
    this.#targetRing = null;
    this.#cart = this.#shutters = this.#lift = null; this.#cartTween = this.#liftTween = null; this.#liftRopes = [];
    this.#figures.forEach(figure => this.#removeFigure(figure)); this.#figures.clear();
    this.#impacts.clear(); this.#contact.clear(); this.#snapshots = []; this.#stop = 0;
    const plate = this.#plate;
    // ponytail: the last three plates' cells stay graded; raise it if a common walk loops through more places.
    this.#gradedPlates = [...this.#gradedPlates.filter(kept => kept !== id), id];
    for (const old of this.#gradedPlates.splice(0, Math.max(0, this.#gradedPlates.length - 3))) {
      for (const key of this.#cells.keys()) if (key.startsWith(`${old}|`)) this.#cells.delete(key);
    }
    this.#integration = INTEGRATION[id] ?? DEFAULT_INTEGRATION;
    this.#ground = this.#samplePlate(this.#integration.ground);
    this.#haze = this.#samplePlate(this.#integration.haze);
    this.#contactTexture();
    this.#scenery.push(scene.add.image(0, 0, id).setOrigin(0).setDepth(-1000));
    this.#propForegrounds = [];
    for (let i = 0; i < plate.foregrounds.length; i++) {
      const foreground = plate.foregrounds[i]!;
      const key = `${id}:foreground:${i}`;
      const left = Math.floor(Math.min(...foreground.polygon.map(point => point[0])));
      const top = Math.floor(Math.min(...foreground.polygon.map(point => point[1])));
      const width = Math.ceil(Math.max(...foreground.polygon.map(point => point[0]))) - left;
      const height = Math.ceil(Math.max(...foreground.polygon.map(point => point[1]))) - top;
      const prepared = foreground.fromProp ? `${key}:${foreground.fromProp}` : key;
      for (const [textureKey, sourceKey] of [[key, id], ...(foreground.fromProp ? [[prepared, `prop:${foreground.fromProp}`]] : [])]) {
        if (scene.textures.exists(textureKey!)) continue;
        const texture = scene.textures.createCanvas(textureKey!, width, height)!;
        const ctx = texture.context;
        ctx.translate(-left, -top);
        ctx.beginPath(); foreground.polygon.forEach(([x, y], j) => j ? ctx.lineTo(x, y) : ctx.moveTo(x, y)); ctx.closePath(); ctx.clip();
        ctx.drawImage(scene.textures.get(sourceKey!).getSourceImage() as HTMLImageElement, 0, 0); texture.refresh();
      }
      const source = foreground.fromProp && this.#shownProps.includes(foreground.fromProp) ? prepared : key;
      const image = scene.add.image(left, top, source).setOrigin(0).setDepth(foreground.depth);
      this.#scenery.push(image);
      if (foreground.fromProp) this.#propForegrounds.push({ prop: foreground.fromProp, image, original: key, prepared });
    }
    this.#ambient!.enter(plate);
    this.#props.clear();
    for (const prop of plate.props ?? []) {
      const image = scene.add.image(prop.at[0], prop.at[1], `prop:${prop.id}`).setOrigin(.5, 1).setDepth(prop.depth ?? prop.at[1]).setVisible(this.#shownProps.includes(prop.id));
      if (prop.height) image.setScale(prop.height / image.height);
      if (prop.crop) image.setCrop(...prop.crop);
      this.#props.set(prop.id, image); this.#scenery.push(image);
    }
    // Movement chalk lies on the painting, beneath props and moving bodies.
    // Target and commitment cues retain their independent overlay layer.
    this.#pathGraphics = scene.add.graphics().setDepth(-900); this.#scenery.push(this.#pathGraphics);
    this.#guidanceGraphics = scene.add.graphics().setDepth(2003); this.#scenery.push(this.#guidanceGraphics);
    this.#targetRing = scene.add.ellipse(0, 0, 34, 15).setStrokeStyle(2, 0xf1b66d, 1).setDepth(2003).setVisible(false);
    this.#scenery.push(this.#targetRing);
    this.#markerGraphics = scene.add.graphics().setDepth(2003); this.#scenery.push(this.#markerGraphics);
    this.#effects = new CombatEffects(scene); this.#scenery.push(this.#effects.graphics);
    if (plate.id === 'silt-stair') {
      for (const x of [202, 243]) {
        const rope = scene.add.rectangle(x, 290, 1.25, 240, 0x857252, .9).setOrigin(0).setDepth(2001);
        this.#liftRopes.push(rope); this.#scenery.push(rope);
      }
      const load = this.#registry.resolve('prop-crate-stack');
      this.#lift = scene.add.image(220, 533, load.textureKey, load.frame).setOrigin(.5, 1).setScale(.85).setDepth(2002);
      this.#scenery.push(this.#lift);
      this.#liftTween = scene.tweens.add({ targets: this.#lift, y: 369, duration: 7800, ease: 'Sine.easeInOut', yoyo: true, repeat: -1, paused: true });
      this.#cart = scene.add.image(250, 598, 'painted-cart').setOrigin(.5, 1).setDepth(598).setVisible(false);
      this.#cart.setScale(36 / this.#cart.height); this.#scenery.push(this.#cart);
      this.#cartTween = scene.tweens.add({ targets: this.#cart, x: 358, duration: 9000, yoyo: true, repeat: -1, paused: true });
      this.#shutters = scene.add.image(973, 359, 'painted-shutters').setOrigin(0).setDepth(2001).setVisible(false); this.#scenery.push(this.#shutters);
    }
    const camera = scene.cameras.main;
    this.#look = null; this.#overview = false;
    camera.zoomEffect.reset(); // an ease begun in the last place must not carry into this one
    // The new place rises out of the dark as its name is revealed; reduced motion cuts straight to it.
    if (this.#reducedMotion) camera.fadeEffect.reset(); else camera.fadeIn(600, ...FADE);
    camera.setBounds(0, 0, plate.width, plate.height).setZoom(this.#fighting ? COMBAT_ZOOM : this.#zoom);
    // The first sight of a place shows the whole painting while its description is read, then eases in to walking distance.
    this.#establishing?.remove(); this.#establishing = null;
    if (!this.#fighting && !this.#established.has(id) && !this.#direct) {
      this.#established.add(id);
      camera.setZoom(Math.min(this.#zoom, Math.max(camera.width / plate.width, camera.height / plate.height)));
      this.#establishing = scene.time.delayedCall(1500, () => {
        this.#establishing = null;
        if (this.#reducedMotion) camera.setZoom(this.#zoom); else camera.zoomTo(this.#zoom, 1600, 'Sine.easeInOut', true);
      });
    }
    this.#drawGuidance(); this.#drawMarker(); this.#syncAftermath();
    this.#snap = true;
  }

  /** Hold the last painting's final frame over the canvas and let it dissolve; display-only, never takes input. */
  #dissolve(): void {
    const canvas = this.#game.canvas;
    if (!canvas.parentElement || !canvas.width) return;
    const still = Object.assign(document.createElement('canvas'), { width: canvas.width, height: canvas.height });
    still.getContext('2d')!.drawImage(canvas, 0, 0);
    Object.assign(still.style, { position: 'absolute', left: `${canvas.offsetLeft}px`, top: `${canvas.offsetTop}px`,
      width: `${canvas.offsetWidth}px`, height: `${canvas.offsetHeight}px`, pointerEvents: 'none', transition: 'opacity 450ms ease-in-out' });
    canvas.after(still);
    requestAnimationFrame(() => { still.style.opacity = '0'; });
    setTimeout(() => still.remove(), 550);
  }
  update(actors: readonly PaintedActor[], paused: boolean, reducedMotion = false): void {
    if (paused && !this.#paused) this.#scene?.cameras.main.zoomEffect.reset();
    this.#actors = actors; this.#paused = paused; this.#reducedMotion = reducedMotion;
    if (reducedMotion) { this.#impacts.clear(); this.#contact.clear(); this.#stop = 0; }
  }
  /** Display-only impact. Call once for each NEW hit event, using the defender ID.
   * It never moves a sim actor or consumes a tick. */
  impact(id: string, options: { from?: Point; sourceId?: string; strength?: number; hitStopMs?: number; flash?: number; shake?: number } = {}): void {
    if (this.#reducedMotion) return;
    const strength = options.strength ?? 4, flash = options.flash ?? 1;
    const source = options.sourceId ? this.#figures.get(options.sourceId) : undefined;
    const from: Point | undefined = source ? [source.x, source.y] : options.from;
    const receiver = this.#actors.find(actor => actor.id === id);
    const striker = this.#actors.find(actor => actor.id === options.sourceId);
    if (receiver && striker) this.#contact.meet(receiver, striker, this.#figures.get(id)?.sprite.scaleX ?? 1, strength);
    this.#impacts.set(id, { ...(from ? { from } : {}),
      strength: Number.isFinite(strength) ? Math.max(0, Math.min(12, strength)) : 4,
      flash: Number.isFinite(flash) ? Math.max(0, Math.min(1, flash)) : 1 });
    this.hitStop(options.hitStopMs ?? 65);
    if (options.shake) this.#scene?.cameras.main.shake(110, Math.min(.01, options.shake));
  }
  /** Display-only mark where a blow met a figure: a cut, bronze sparks, a breaking weapon, or dust as a body falls.
   * Reduced motion keeps it, fainter and still. */
  burst(id: string, kind: BurstKind, options: { from?: Point; sourceId?: string; power?: number } = {}): void {
    const figure = this.#figures.get(id);
    if (!figure || !this.#effects) return;
    const scale = figure.sprite.scaleY, source = options.sourceId ? this.#figures.get(options.sourceId) : undefined;
    const from: Point | undefined = source ? [source.sprite.x, source.sprite.y] : options.from;
    const angle = from ? Math.atan2(figure.sprite.y - from[1], figure.sprite.x - from[0]) : 0;
    // Chest height for a cut, shield height for a block, the weapon arm for a break; the side facing the blow.
    const lift = { hit: 34, block: 30, break: 42, dust: 0, swing: 34, thrust: 34, arrow: 34, treat: 28 }[kind], toward = kind === 'block' || kind === 'break' ? 10 : 0;
    const death = this.#registry.animation(figure.appearance)?.clips.death;
    const delay = kind === 'dust' && death && !this.#reducedMotion ? death.frames.length / death.fps * 700 : 0;
    this.#effects.add(kind, figure.sprite.x - Math.cos(angle) * toward * scale, figure.sprite.y - lift * scale, angle, scale, options.power ?? .5, delay, from ? [from[0], from[1] - lift * (source?.sprite.scaleY ?? scale)] : undefined);
  }
  hitStop(ms = 65): void {
    if (Number.isFinite(ms)) this.#stop = Math.max(this.#stop, Math.max(0, Math.min(120, ms)));
  }
  /** Read-only presentation evidence; no game state can be changed through it. */
  motionSnapshot(): readonly ActorMotionSnapshot[] {
    let pool = this.#snapshotPools.get(this.#snapshots.length);
    if (!pool) {
      // ponytail: 512 buffers preserve over four seconds even with two 60 Hz readers; enlarge only for longer QA captures.
      pool = { cursor: 0, buffers: Array.from({ length: 512 }, () => this.#snapshots.map(value => ({ ...value }))) };
      this.#snapshotPools.set(this.#snapshots.length, pool);
    }
    const result = pool.buffers[pool.cursor]!, source = this.#snapshots;
    pool.cursor = (pool.cursor + 1) % pool.buffers.length;
    for (let i = 0; i < source.length; i++) {
      const from = source[i]!, to = result[i]!;
      to.id = from.id; to.x = from.x; to.y = from.y; to.footX = from.footX; to.footY = from.footY;
      to.frame = from.frame; to.clip = from.clip; to.depth = from.depth; to.shadowX = from.shadowX;
      to.shadowY = from.shadowY; to.scale = from.scale; to.flipX = from.flipX;
    }
    return result;
  }
  marker(point: Point): void { this.#marker = point; this.#drawMarker(); }
  guidance(value: PaintedGuidance): void {
    if (this.#sameGuidance(value)) return;
    this.#guidance = value; this.#drawGuidance();
  }
  /** Show exactly these of the current plate's props, and light its story fires (`burnings`) by id. */
  props(visible: readonly string[]): void {
    this.#shownProps = visible;
    for (const [id, image] of this.#props) image.setVisible(visible.includes(id));
    for (const foreground of this.#propForegrounds) {
      const key = visible.includes(foreground.prop) ? foreground.prepared : foreground.original;
      if (foreground.image.texture.key !== key) foreground.image.setTexture(key);
    }
    this.#ambient?.show(visible);
  }
  aftermath(value: { carts: string; shutters: string; gate: string; activeBodies: number }): void { this.#world = value; this.#syncAftermath(); }
  point(x: number, y: number): Point {
    const p = this.#scene?.cameras.main.getWorldPoint(x, y);
    return p ? [p.x, p.y] : [0, 0];
  }
  screen(x: number, y: number): Point {
    const camera = this.#scene?.cameras.main;
    return camera ? [(x - camera.worldView.x) * camera.zoom, (y - camera.worldView.y) * camera.zoom] : [0, 0];
  }
  pick(x: number, y: number): string | null {
    return [...this.#actors].sort((a, b) => (this.#figures.get(b.id)?.sprite.y ?? b.y)
      - (this.#figures.get(a.id)?.sprite.y ?? a.y)).find(actor => {
      const figure = this.#figures.get(actor.id), scale = figure?.sprite.scaleX ?? 1;
      return !actor.dead && !!figure && Math.abs(x - figure.sprite.x) < 24 * scale
        && y > figure.sprite.y - 70 * scale && y < figure.sprite.y + 6 * scale;
    })?.id ?? null;
  }
  get camera(): { x: number; y: number; zoom: number; detached: boolean; overview: boolean } {
    const camera = this.#scene?.cameras.main;
    return { x: camera?.worldView.x ?? 0, y: camera?.worldView.y ?? 0, zoom: camera?.zoom ?? this.#zoom, detached: !!this.#look, overview: this.#overview };
  }
  /** The visible part of the canvas can be narrower than its backing surface on a phone. */
  viewport(width: number, height: number): void {
    const fit = Math.min(width / this.#plate.width, height / this.#plate.height);
    if (fit === this.#minimumZoom) return;
    this.#minimumZoom = fit;
    if (this.#overview) this.survey();
  }
  #lookAt(at: Point): void {
    const camera = this.#scene?.cameras.main;
    if (!camera) return;
    this.#establishing?.remove(); this.#establishing = null; camera.zoomEffect.reset();
    const clamp = (value: number, size: number, span: number): number => span >= size ? size / 2 : Math.max(span / 2, Math.min(size - span / 2, value));
    this.#look = [clamp(at[0], this.#plate.width, camera.width / camera.zoom), clamp(at[1], this.#plate.height, camera.height / camera.zoom)];
    // Phaser clamps a smaller-than-viewport painting to its top-left. Our centre
    // clamp also handles letterboxing, so surveying stays centred on narrow screens.
    camera.useBounds = false; camera.centerOn(...this.#look);
  }
  /** Pan is view movement only, including while simulation is paused. Units are canvas pixels. */
  pan(dx: number, dy: number): void {
    const camera = this.#scene?.cameras.main;
    if (!camera || this.#direct) return;
    this.#overview = false;
    const center = this.#look ?? [camera.scrollX + camera.width / 2, camera.scrollY + camera.height / 2];
    this.#lookAt([center[0]! + dx / camera.zoom, center[1]! + dy / camera.zoom]);
  }
  zoom(factor: number, anchor?: Point): void {
    const camera = this.#scene?.cameras.main;
    if (!camera || this.#direct) return;
    const at = anchor ?? [camera.width / 2, camera.height / 2];
    const center = this.#look ?? [camera.scrollX + camera.width / 2, camera.scrollY + camera.height / 2];
    const before = camera.zoom, after = Math.max(this.#minimumZoom, Math.min(3, before * factor));
    this.#overview = false; camera.zoomEffect.reset(); camera.setZoom(after);
    this.#lookAt([center[0]! + (at[0] - camera.width / 2) * (1 / before - 1 / after), center[1]! + (at[1] - camera.height / 2) * (1 / before - 1 / after)]);
  }
  /** Fit the actual painting inside the visible viewport, without changing any story knowledge. */
  survey(): void {
    const camera = this.#scene?.cameras.main;
    if (!camera || this.#direct) return;
    camera.setZoom(this.#minimumZoom);
    this.#lookAt([this.#plate.width / 2, this.#plate.height / 2]); this.#overview = true;
  }
  follow(): void {
    this.#look = null; this.#overview = false;
    this.#establishing?.remove(); this.#establishing = null;
    const camera = this.#scene?.cameras.main;
    if (camera) camera.useBounds = true;
    camera?.zoomEffect.reset(); camera?.setZoom(this.#fighting ? COMBAT_ZOOM : this.#zoom); this.#snap = true;
  }
  /** Direct the camera (the opening), or hand it back to following your character at walking distance. */
  direct(shot: { at: Point; closeness: number } | null): void {
    if (!this.#direct && shot) { this.#establishing?.remove(); this.#establishing = null; this.#established.add(this.#plate.id); this.#scene?.cameras.main.zoomEffect.reset(); }
    if (this.#direct && !shot) { this.#scene?.cameras.main.setZoom(this.#fighting ? COMBAT_ZOOM : this.#zoom); this.#snap = true; }
    this.#direct = shot;
  }
  /** A fight draws the camera in so the figures read at 1280x720; it eases back out when the fight ends. */
  combat(active: boolean): void {
    const camera = this.#scene?.cameras.main;
    if (!camera || this.#fighting === active) return;
    this.#fighting = active; camera.useBounds = true;
    this.#look = null; this.#overview = false;
    this.#establishing?.remove(); this.#establishing = null;
    const zoom = active ? COMBAT_ZOOM : this.#zoom;
    if (this.#reducedMotion || this.#paused) { camera.zoomEffect.reset(); camera.setZoom(zoom); this.#snap = true; }
    else camera.zoomTo(zoom, 450, 'Sine.easeInOut', true);
  }

  #sameGuidance(value: PaintedGuidance): boolean {
    if (value.targetId !== this.#guidance.targetId) return false;
    if (value.path.length !== this.#guidance.path.length || value.telegraphs.length !== this.#guidance.telegraphs.length) return false;
    if (!!value.target !== !!this.#guidance.target || value.target && (value.target[0] !== this.#guidance.target![0] || value.target[1] !== this.#guidance.target![1])) return false;
    for (let i = 0; i < value.path.length; i++) if (value.path[i]![0] !== this.#guidance.path[i]![0] || value.path[i]![1] !== this.#guidance.path[i]![1]) return false;
    for (let i = 0; i < value.telegraphs.length; i++) {
      const a = value.telegraphs[i]!, b = this.#guidance.telegraphs[i]!;
      if (a.from[0] !== b.from[0] || a.from[1] !== b.from[1] || a.to[0] !== b.to[0] || a.to[1] !== b.to[1] || a.progress !== b.progress || a.hostile !== b.hostile) return false;
    }
    return true;
  }

  #drawGuidance(): void {
    const g = this.#guidanceGraphics, path = this.#pathGraphics;
    if (!g || !path) return;
    this.#targetRing?.setVisible(!!this.#guidance.target);
    if (this.#guidance.target) {
      const body = this.#guidance.targetId ? this.#figures.get(this.#guidance.targetId)?.sprite : undefined;
      this.#targetRing?.setPosition(body?.x ?? this.#guidance.target[0], body?.y ?? this.#guidance.target[1]);
    }
    path.setVisible(!!this.#guidance.path.length).clear();
    path.lineStyle(1.5, 0xe6ce91, .8);
    for (let i = 0; i < this.#guidance.path.length; i++) {
      const point = this.#guidance.path[i]!;
      if (i) { const previous = this.#guidance.path[i - 1]!; path.lineBetween(previous[0], previous[1], point[0], point[1]); }
      path.strokeEllipse(point[0], point[1], 5, 3);
    }
    g.setVisible(!!this.#guidance.telegraphs.length).clear();
    for (const tell of this.#guidance.telegraphs) {
      const [x, y] = tell.from, [tx, ty] = tell.to;
      const dx = tx - x, dy = ty - y, length = Math.hypot(dx, dy);
      if (length < 1) continue;
      const ux = dx / length, uy = dy / length, color = tell.hostile ? 0xd3977c : 0xdac694;
      const start = Math.min(13, length * .2), end = Math.max(start, length - 17);
      // A directed line describes a commitment, never an area of damage.
      // The dark under-stroke keeps the diagram readable on chalk and brick.
      g.lineStyle(4, 0x191b1c, .7).lineBetween(x + ux * start, y + uy * start, x + ux * end, y + uy * end);
      g.lineStyle(1, color, .55).lineBetween(x + ux * start, y + uy * start, x + ux * end, y + uy * end);
      const progress = Math.max(0, Math.min(1, tell.progress)), front = start + (end - start) * progress;
      g.lineStyle(2.5, color, .9).lineBetween(x + ux * start, y + uy * start, x + ux * front, y + uy * front);
      for (let i = 1; i <= 4; i++) {
        const d = start + (end - start) * i / 5, px = x + ux * d, py = y + uy * d;
        g.lineStyle(1, color, i / 5 <= progress ? .95 : .3).lineBetween(px - uy * 2.5, py + ux * 2.5, px + uy * 2.5, py - ux * 2.5);
      }
      const ax = x + ux * end, ay = y + uy * end;
      g.fillStyle(color, .95).fillTriangle(ax, ay, ax - ux * 7 - uy * 4, ay - uy * 7 + ux * 4, ax - ux * 7 + uy * 4, ay - uy * 7 - ux * 4);
      g.lineStyle(1.5, color, .8).strokeEllipse(tx, ty, 28, 12);
      if (tell.hostile) {
        // Two teeth distinguish incoming danger even without colour.
        g.lineStyle(2, color, .9).lineBetween(tx - 18, ty - 3, tx - 15, ty).lineBetween(tx + 18, ty - 3, tx + 15, ty);
      }
    }
  }

  #drawMarker(): void {
    const g = this.#markerGraphics;
    if (!g) return;
    g.setVisible(!!this.#marker).clear();
    if (this.#marker) g.lineStyle(1, 0xe1c889, .7).strokeEllipse(this.#marker[0], this.#marker[1], 16, 7);
  }

  #syncAftermath(): void {
    if (!this.#cart || !this.#shutters) return;
    this.#cart.setVisible(!!this.#world);
    this.#shutters.setVisible(this.#world?.shutters === 'closed');
    if (this.#world && (this.#world.carts === 'moving' || this.#world.carts === 'porters')) this.#cartTween?.resume();
    else { this.#cartTween?.pause(); this.#cart.setX(250); }
    // A repaired hoist carries the illustrated load. A manual crew leaves it idle.
    if (this.#world?.carts === 'moving' || this.#world?.carts === 'rationed') {
      this.#liftTween?.setTimeScale(this.#world.carts === 'rationed' ? .45 : 1).resume();
    } else { this.#liftTween?.pause(); this.#lift?.setY(533); }
  }

  #draw(delta: number): void {
    const scene = this.#scene;
    if (!scene) return;
    const stopped = this.#stop > 0;
    if (!this.#paused) this.#stop = Math.max(0, this.#stop - delta);
    const advance = !this.#paused && !stopped;
    const step = advance && !this.#reducedMotion ? delta : 0;
    scene.tweens.timeScale = advance && !this.#reducedMotion ? 1 : 0;
    this.#time += step;
    this.#contact.tick(step);
    this.#ambient!.tick(step, this.#reducedMotion);
    const drawId = ++this.#drawId, view = scene.cameras.main.worldView;
    let snapshotCount = 0, visibleActors = 0, pc: PaintedActor | undefined;
    for (const actor of this.#actors) {
      if (actor.id === this.#focus) pc = actor;
      if (actor.id !== this.#focus && (actor.x < view.x - 128 || actor.x > view.right + 128 || actor.y < view.y - 128 || actor.y > view.bottom + 128)) continue;
      visibleActors++;
      const animation = this.#registry.animation(actor.appearance);
      let figure = this.#figures.get(actor.id);
      const info = animation ? undefined : figure?.appearance === actor.appearance && figure.infoFacing === actor.facing
        ? figure.spriteInfo : this.#registry.resolve(actor.appearance, actor.facing);
      if (!figure) {
        const baseTexture = animation ? `actor:${actor.appearance}` : info!.textureKey;
        const snapshot = { id: actor.id, x: actor.x, y: actor.y, footX: actor.x, footY: actor.y,
          frame: 0, clip: actor.dead ? 'corpse' : 'idle' as ActorClipName, depth: actor.y,
          shadowX: actor.x, shadowY: actor.y, scale: 1, flipX: false };
        figure = { sprite: scene.add.sprite(actor.x, actor.y, baseTexture, 0),
          skin: undefined, light: '', warmth: -1, daylight: -1, fog: -1,
          shadow: scene.add.image(actor.x, actor.y, 'painted-shadow').setDisplaySize(35, 12),
          ring: scene.add.ellipse(actor.x, actor.y, 28, 11).setStrokeStyle(1, 0xd8c68e, .55),
          telegraph: scene.add.graphics(), x: actor.x, y: actor.y, distance: 0, elapsed: 0,
          clip: actor.dead ? 'corpse' : 'idle', dead: actor.dead, attacking: false, flash: 0,
          impactAge: 1000, knock: [0, 0], flashPower: 0, deadFor: actor.dead ? 1e4 : 0, dim: 0, facing: actor.facing, action: actor.action,
          appearance: actor.appearance, frame: -1, seen: drawId,
          pose: { frame: 0, flipX: false }, snapshot,
          drawnAction: undefined, telegraphX: NaN, telegraphY: NaN, spriteInfo: info, infoFacing: actor.facing };
        this.#figures.set(actor.id, figure);
      }
      if (!animation) { figure.spriteInfo = info; figure.infoFacing = actor.facing; }
      figure.seen = drawId;
      if (actor.flash > figure.flash + .05 && !actor.dead) this.impact(actor.id);
      figure.flash = actor.flash;
      const impact = this.#impacts.get(actor.id);
      if (impact) {
        const dx = impact.from ? actor.x - impact.from[0] : 1, dy = impact.from ? actor.y - impact.from[1] : 0;
        const length = Math.hypot(dx, dy) || 1;
        figure.knock = [dx / length * impact.strength, dy / length * impact.strength];
        figure.flashPower = impact.flash; figure.impactAge = 0; this.#impacts.delete(actor.id);
      }
      const travelled = advance ? Math.hypot(actor.x - figure.x, actor.y - figure.y) : 0;
      const perspective = this.#integration.perspective;
      const distance = this.#distance(advance ? actor.y : figure.y);
      const scale = (animation ? 1 : .9) * (perspective[2] + (perspective[3] - perspective[2]) * distance);
      // The host's sim-tick window can begin on the tick that auto-pauses, or
      // resolve on the hit-stop frame. Publish that pose before holding it.
      figure.action = actor.action;
      if (actor.action || actor.guard) figure.facing = actor.facing;
      if (advance) {
        figure.x = actor.x; figure.y = actor.y; figure.facing = actor.facing;
        // The painted body's stride shrinks with it; retain planted motion
        // when a traveller crosses the perspective calibration.
        figure.distance += Math.min(travelled, 32) / scale; figure.impactAge += step;
      }
      if (this.#reducedMotion) { figure.impactAge = 1000; figure.knock = [0, 0]; }
      const contact = !actor.dead && !this.#reducedMotion ? this.#contact.pose(actor.id) : undefined;
      const incoming = !actor.dead && !actor.moving && this.#actors.find(other => !other.dead
        && other.action?.targetId === actor.id && other.action.phase === 'windup'
        && !other.appearance.includes('scout') && Math.hypot(other.x - actor.x, other.y - actor.y) < 88 * scale);
      if (!actor.action && !actor.moving) {
        if (contact) figure.facing = contact.facing;
        else if (incoming) figure.facing = ['e', 'se', 's', 'sw', 'w', 'nw', 'n', 'ne']
          [(Math.round(Math.atan2(incoming.y - actor.y, incoming.x - actor.x) / (Math.PI / 4)) + 8) % 8]!;
      }
      let clip: ActorClipName;
      if (this.#reducedMotion) clip = actor.dead ? 'corpse' : 'idle';
      else if (actor.dead) clip = !figure.dead || figure.clip === 'death' ? 'death' : 'corpse';
      else if (contact && (!figure.action || figure.action.phase === 'recovery' || figure.impactAge < 200)) clip = contact.clip;
      else if (figure.impactAge < 200) clip = 'recoil';
      else if (figure.action || actor.attacking && actor.appearance !== 'actor-working-dead') clip = 'attack';
      else if (actor.moving && (travelled > .01 || figure.clip === 'walk')) clip = 'walk';
      else if (contact) clip = contact.clip;
      else if (actor.guard || incoming) clip = 'receive';
      else if (actor.activity === 'work' || actor.activity === undefined && actor.appearance === 'actor-working-dead') clip = 'work';
      else clip = 'idle';
      if (clip !== figure.clip || actor.attacking && !figure.attacking) figure.elapsed = 0;
      else figure.elapsed += step;
      // A body falls where its feet were planted, including an unfinished bind.
      if (actor.dead && !figure.dead && !this.#reducedMotion)
        figure.knock = [figure.sprite.x - figure.x, figure.sprite.y - figure.y];
      figure.clip = clip; figure.dead = actor.dead; figure.attacking = actor.attacking;
      if (clip === 'death' && animation && figure.elapsed >= animation.clips.death!.frames.length / animation.clips.death!.fps * 1000) {
        clip = figure.clip = 'corpse'; figure.elapsed = 0;
      }
      const pose = figure.pose;
      if (animation) actorFrame(animation, clip, figure.facing, figure.elapsed, figure.distance, figure.action, pose);
      else { pose.frame = typeof info!.frame === 'number' ? info!.frame : 0; pose.flipX = false; }
      const originX = animation ? animation.foot[0] / animation.cell.width : .5;
      const originY = animation ? animation.foot[1] / animation.cell.height : info!.anchorY;
      const recoil = actor.dead ? 1 : Math.pow(Math.max(0, 1 - figure.impactAge / 240), 2) * (contact ? .18 : 1);
      let approachX = 0, approachY = 0;
      const opponent = !contact && !actor.dead && !this.#reducedMotion && !actor.moving
        && !actor.appearance.includes('scout') && (actor.action
          ? this.#actors.find(other => other.id === actor.action!.targetId && !other.dead) : incoming);
      if (opponent) {
        const dx = opponent.x - actor.x, dy = opponent.y - actor.y, gap = Math.hypot(dx, dy);
        const window = actor.action ?? opponent.action!;
        const progress = window.phase === 'windup' ? Math.max(0, Math.min(1, window.progress))
          : window.phase === 'commit' ? 1 : 1 - Math.max(0, Math.min(1, window.progress));
        const share = actor.action ? opponent.action?.targetId === actor.id ? .5 : .82 : .18;
        const weight = gap <= 72 * scale ? progress * progress * (3 - 2 * progress) : 0;
        const close = Math.max(0, gap - 26 * scale) * share * weight, shoulder = 8 * scale * weight;
        approachX = (dx * close + dy * shoulder) / (gap || 1);
        approachY = (dy * close - dx * shoulder) / (gap || 1);
      }
      const x = figure.x + figure.knock[0]*recoil + (contact?.x ?? 0)*scale + approachX;
      const y = figure.y + figure.knock[1]*recoil + (contact?.y ?? 0)*scale + approachY;
      const breathing = clip === 'idle' && !this.#reducedMotion ? Math.sin((this.#time + actor.id.length*91) / 620)*.004 : 0;
      let keyLight = 0;
      // Hoisted per-actor invariants; falloff squared directly instead of
      // Math.pow. Signed contributions still cancel smoothly between lanterns
      // and directly underneath one, so a mirrored facing or crossing cannot
      // flip the key.
      const lamps = this.#plate.lamps, lampCount = lamps.length, glowY = 35 * scale;
      for (let i = 0; i < lampCount; i++) {
        const lamp = lamps[i]!;
        const dx = lamp[0] - x, dy = lamp[1] - y + glowY, distance = Math.hypot(dx, dy);
        const falloff = Math.max(0, 1 - distance / 230);
        keyLight += dx / Math.max(25, distance) * falloff * falloff;
      }
      const warmth = Math.min(1, Math.abs(keyLight) * 1.6);
      const side = (keyLight < 0) !== pose.flipX ? 'left' : 'right';
      const flashAlpha = animation && !actor.dead && figure.flashPower
        ? Math.max(0, 1 - figure.impactAge / 140) * (contact ? .12 + .16 * figure.flashPower : .18 + .34 * figure.flashPower) : 0;
      // Death: the fall, a held moment, then the body dims into the ground. Real time, so a pause can hold it too.
      figure.deadFor = actor.dead ? figure.deadFor + step : 0;
      const fall = animation?.clips.death ? animation.clips.death.frames.length / animation.clips.death.fps * 1000 : 0;
      const dim = this.#reducedMotion && actor.dead ? .4 : Math.round(Math.max(0, Math.min(1, (figure.deadFor - fall - 900) / 1800)) * .4 * 32) / 32;
      const overlay = flashAlpha > 0 ? 'flash' : side;
      // Quantized blend drivers: a walking actor repaints its cached skin only
      // when the pose or a visible light step changes, not on every sub-pixel
      // move. Steps stay far below one grey level per pixel, so the plate
      // match is unaffected; the repaint key below compares these directly.
      const warmthAlpha = Math.round((flashAlpha || (animation ? warmth * .85 : 0)) * 32) / 32;
      const daylight = animation ? Math.round(this.#ambient!.daylightAt(x, y - glowY) * 32) / 32 : 0;
      const fog = animation ? Math.round((1 - distance) * this.#integration.fog * 128) / 128 : 0;
      if (animation) {
        const { width, height } = animation.cell;
        const skin = figure.skin ??= scene.textures.createCanvas(`figure:${actor.id}`, width, height)!;
        if (figure.frame !== pose.frame || figure.appearance !== actor.appearance
          || figure.light !== overlay || figure.warmth !== warmthAlpha || figure.daylight !== daylight || figure.fog !== fog || figure.dim !== dim) {
          if (skin.width !== width || skin.height !== height) skin.setSize(width, height);
          const c = skin.context;
          c.clearRect(0, 0, width, height);
          const cell = this.#skin(actor.appearance, pose.frame);
          for (const [variant, alpha] of [['cool', 1], ['day', daylight], [overlay, warmthAlpha], ['fog', fog]] as const) {
            if (alpha <= .005) continue;
            c.globalAlpha = alpha;
            c.drawImage(cell[variant]!, 0, 0);
          }
          c.globalAlpha = 1;
          if (dim) { c.globalCompositeOperation = 'source-atop'; c.fillStyle = `rgba(12,14,17,${dim})`; c.fillRect(0, 0, width, height); c.globalCompositeOperation = 'source-over'; }
          skin.refresh();
          figure.light = overlay; figure.warmth = warmthAlpha; figure.daylight = daylight; figure.fog = fog; figure.dim = dim;
        }
        if (figure.sprite.texture.key !== skin.key) figure.sprite.setTexture(skin.key);
      } else if (figure.sprite.texture.key !== info!.textureKey || figure.frame !== pose.frame) {
        figure.sprite.setTexture(info!.textureKey, pose.frame);
      }
      figure.frame = pose.frame; figure.appearance = actor.appearance;
      figure.sprite.setOrigin(originX, originY).setPosition(x, y).setDepth(y)
        .setScale(scale, scale*(1+breathing)).setFlipX(pose.flipX).setAngle(0);
      figure.shadow.setPosition(x, y).setDepth(y - .2).setDisplaySize((actor.dead ? 38 + 19 * (this.#reducedMotion ? 1 : Math.min(1, figure.deadFor / Math.max(1, fall))) : 38) * scale, 15 * scale);
      figure.ring.setPosition(x, y + 1).setDepth(y - .1).setScale(scale)
        .setVisible(actor.id === this.#focus && !actor.dead && !!actor.action);
      if (actor.id === this.#guidance.targetId) this.#targetRing?.setPosition(x, y);
      this.#telegraph(figure, actor);
      const snapshot = figure.snapshot;
      snapshot.x = figure.x; snapshot.y = figure.y; snapshot.footX = x; snapshot.footY = y;
      snapshot.frame = pose.frame; snapshot.clip = clip; snapshot.depth = y;
      snapshot.shadowX = figure.shadow.x; snapshot.shadowY = figure.shadow.y; snapshot.scale = scale; snapshot.flipX = pose.flipX;
      this.#snapshots[snapshotCount++] = snapshot;
    }
    this.#snapshots.length = snapshotCount;
    this.#effects?.draw(this.#paused ? 0 : delta, this.#reducedMotion);
    for (const [id, figure] of this.#figures) if (figure.seen !== drawId) {
      this.#removeFigure(figure); this.#figures.delete(id); this.#impacts.delete(id);
    }
    if (this.#direct) {
      const camera = scene.cameras.main, plate = this.#plate, wide = Math.min(this.#zoom, Math.max(camera.width / plate.width, camera.height / plate.height));
      camera.setZoom(wide + (this.#zoom - wide) * this.#direct.closeness).centerOn(this.#direct.at[0], this.#direct.at[1]);
    } else if (this.#look) {
      scene.cameras.main.centerOn(...this.#look);
    } else if (pc) {
      const camera = scene.cameras.main;
      const targetX = pc.x - camera.width / 2, targetY = pc.y - camera.height / 2;
      const rate = this.#snap || this.#reducedMotion ? 1 : 1 - Math.exp(-step / 140);
      camera.setScroll(camera.scrollX + (targetX - camera.scrollX) * rate, camera.scrollY + (targetY - camera.scrollY) * rate);
      this.#snap = false;
    }
    if (this.#marker) {
      const [x, y] = this.#marker;
      if (pc && Math.hypot(pc.x - x, pc.y - y) < 8) { this.#marker = null; this.#drawMarker(); }
    }
    if (this.#lift) for (let i = 0; i < this.#liftRopes.length; i++) this.#liftRopes[i]!.setDisplaySize(1.25, this.#lift.y - 293 - i * 8);
    const renderer = this.#game.renderer as Phaser.Renderer.Canvas.CanvasRenderer;
    this.#performance.fps = this.#game.loop.actualFps;
    this.#performance.drawCount = renderer.drawCount;
    this.#performance.displayObjects = scene.children.length;
    this.#performance.actors = this.#actors.length;
    this.#performance.visibleActors = visibleActors;
    this.#performance.culledActors = this.#actors.length - visibleActors;
  }
  #telegraph(figure: Figure, actor: PaintedActor): void {
    const action = actor.dead ? undefined : figure.action, previous = figure.drawnAction;
    const x = figure.sprite.x, y = figure.sprite.y;
    if (figure.telegraphX === x && figure.telegraphY === y && action?.phase === previous?.phase
      && action?.progress === previous?.progress && action?.toward?.[0] === previous?.toward?.[0]
      && action?.toward?.[1] === previous?.toward?.[1]
      && actor.guard?.[0] === figure.drawnGuard?.[0] && actor.guard?.[1] === figure.drawnGuard?.[1]) return;
    figure.drawnAction = action; figure.telegraphX = x; figure.telegraphY = y;
    figure.drawnGuard = actor.guard;
    const g = figure.telegraph; g.setVisible(!!action || !!actor.guard && !actor.dead).clear().setDepth(y - .05);
    if (actor.guard && !actor.dead) {
      const angle = Math.atan2(actor.guard[1] - y, actor.guard[0] - x);
      for (let i = 0; i < 8; i++) {
        const a = angle - .65 + i * .16, b = a + .16;
        g.lineStyle(3, 0xc3b18b, .85).lineBetween(x + Math.cos(a) * 24, y + Math.sin(a) * 15,
          x + Math.cos(b) * 24, y + Math.sin(b) * 15);
      }
    }
    if (!action) return;
    const { phase, toward } = action;
    const progress = Math.max(0, Math.min(1, action.progress));
    if (phase === 'windup' || phase === 'commit') {
      const angle = toward ? Math.atan2(toward[1] - y, toward[0] - x) : 0;
      const color = phase === 'commit' ? 0xe9ddbf : 0xbfa578;
      // A small clock at the striker's feet. The long line to the victim
      // belongs to guidance, so the figure never stands in a filled cone.
      g.lineStyle(3, 0x191b1c, .65).strokeEllipse(x, y, 35, 15);
      g.lineStyle(1.5, color, .85);
      for (let i = 0; i < 16 * (phase === 'commit' ? 1 : progress); i++) {
        const a = angle + i / 16 * Math.PI * 2, b = a + .28;
        g.lineBetween(x + Math.cos(a) * 17.5, y + Math.sin(a) * 7.5, x + Math.cos(b) * 17.5, y + Math.sin(b) * 7.5);
      }
      return;
    }
    g.lineStyle(2, 0x98a8ac, .85);
    g.lineBetween(x-14, y+7, x-14+28*(1-progress), y+7);
  }
  dispose(): void { delete this.#target.__paintedPerformance; this.#game.destroy(true); }
}
